/**
 * ToneAudioEngine — Tone.js-backed audio engine implementing IAudioEngine.
 *
 * Uses Tone.Player in standalone mode (NOT Tone.Transport) to preserve
 * render loop control over playhead advancement. Tracks clock offset
 * manually just like the legacy AudioEngine.
 *
 * Multi-track capable: manages per-track Tone.Channel nodes for
 * volume/pan/solo/mute when Phase 3 features are used.
 */
import * as Tone from 'tone';
import type { IAudioEngine } from './audioEngineInterface';
import { EffectsManager } from './effectsManager';
import type { EffectInstance, EffectClip } from '../types';
import { createToneEffect, hasMixAPI } from './effectsManager';

type ToneEffect = Tone.Reverb | Tone.Freeverb | Tone.FeedbackDelay | Tone.Chorus | Tone.Distortion |
  Tone.Compressor | Tone.EQ3 | Tone.Phaser | Tone.Tremolo | Tone.BitCrusher | Tone.AutoFilter | Tone.Gain;

interface TrackChannel {
  id: string;
  channel: Tone.Channel;
  player: Tone.Player | null;
  volume: number; // dB
  pan: number; // -1 to 1
  muted: boolean;
  soloed: boolean;
}

export class ToneAudioEngine implements IAudioEngine {
  private ctx: Tone.BaseContext;
  private buffer: Tone.ToneAudioBuffer | null = null;
  private buffers = new Map<string, Tone.ToneAudioBuffer>();
  private player: Tone.Player | null = null;
  private masterGain: Tone.Gain;

  // Clock sync (same approach as legacy engine)
  private playStartCtxTime = 0;
  private playStartOffsetMs = 0;
  private _playing = false;

  // Multi-track support (Phase 3+)
  private tracks = new Map<string, TrackChannel>();
  private anySoloed = false;

  // Effects
  private effectsManager = new EffectsManager();
  private preEffectsGains = new Map<string, Tone.Gain>();
  private preMasterGain = new Tone.Gain(1);

  // Effect clip nodes (Region-based Event FX) — nested per parent track
  private effectClipNodes = new Map<string, Map<string, {
    toneEffect: ToneEffect;
    wetValue: number;
    active: boolean;
  }>>();

  // Per-track analyser nodes for VU meters (Phase 8)
  private analysers = new Map<string, Tone.Analyser>();
  private masterAnalyser: Tone.Analyser | null = null;

  constructor() {
    this.ctx = Tone.getContext();
    this.masterGain = new Tone.Gain(1);
    this.preMasterGain.toDestination();
    this.masterGain.connect(this.preMasterGain);
  }

  async init(proxyData: ArrayBuffer): Promise<void> {
    const rawCtx = this.ctx.rawContext as AudioContext;
    const audioBuffer = await rawCtx.decodeAudioData(proxyData.slice(0));
    this.buffer = new Tone.ToneAudioBuffer(audioBuffer);
  }

  async initAsset(assetId: string, proxyData: ArrayBuffer): Promise<void> {
    const rawCtx = this.ctx.rawContext as AudioContext;
    const audioBuffer = await rawCtx.decodeAudioData(proxyData.slice(0));
    const buf = new Tone.ToneAudioBuffer(audioBuffer);
    this.buffers.set(assetId, buf);
    // Keep legacy buffer pointing to the first/latest for backward compat
    if (!this.buffer) this.buffer = buf;
  }

  play(startTimeMs: number, assetId?: string): void {
    const buf = assetId ? this.buffers.get(assetId) ?? this.buffer : this.buffer;
    if (!buf) return;
    this.stop();

    // Resume context if suspended (autoplay policy)
    if (this.ctx.state !== 'running') {
      Tone.start();
    }

    this.player = new Tone.Player(buf);
    // Route through first audio track channel (for track effects), or direct to masterGain
    const firstTrack = this.tracks.size > 0 ? this.tracks.values().next().value : null;
    if (firstTrack) {
      this.player.connect(firstTrack.channel);
    } else {
      this.player.connect(this.masterGain);
    }

    const offsetSec = Math.max(0, startTimeMs / 1000);
    const bufferDuration = buf.duration;

    if (offsetSec >= bufferDuration) return;

    this.player.start(Tone.now(), offsetSec);

    this.playStartCtxTime = this.ctx.rawContext.currentTime;
    this.playStartOffsetMs = startTimeMs;
    this._playing = true;

    this.player.onstop = () => {
      this._playing = false;
    };
  }

  stop(): void {
    if (this.player) {
      try { this.player.stop(); } catch { /* already stopped */ }
      this.player.disconnect();
      this.player.dispose();
      this.player = null;
    }
    this._playing = false;
  }

  getCurrentTimeMs(): number {
    if (!this._playing) return this.playStartOffsetMs;
    const elapsed = (this.ctx.rawContext.currentTime - this.playStartCtxTime) * 1000;
    return this.playStartOffsetMs + elapsed;
  }

  get playing(): boolean {
    return this._playing;
  }

  setVolume(volume: number): void {
    const clamped = Math.max(0, Math.min(1, volume));
    this.masterGain.gain.value = clamped;
  }

  get durationMs(): number {
    return this.buffer ? this.buffer.duration * 1000 : 0;
  }

  // --- Multi-track methods (Phase 3) ---

  addTrack(trackId: string): void {
    if (this.tracks.has(trackId)) return;
    const channel = new Tone.Channel(0, 0); // 0dB, center pan
    const preEffectsGain = new Tone.Gain(1);
    channel.connect(preEffectsGain);
    preEffectsGain.connect(this.masterGain);
    this.preEffectsGains.set(trackId, preEffectsGain);
    this.tracks.set(trackId, {
      id: trackId,
      channel,
      player: null,
      volume: 0,
      pan: 0,
      muted: false,
      soloed: false,
    });
  }

  removeTrack(trackId: string): void {
    const t = this.tracks.get(trackId);
    if (!t) return;
    t.player?.stop();
    t.player?.dispose();
    t.channel.dispose();
    this.preEffectsGains.get(trackId)?.dispose();
    this.preEffectsGains.delete(trackId);
    this.effectsManager.clearChain(trackId);
    this.tracks.delete(trackId);
    this.updateSoloState();
  }

  setTrackVolume(trackId: string, volumeLinear: number): void {
    const t = this.tracks.get(trackId);
    if (!t) return;
    const db = volumeLinear <= 0 ? -Infinity : 20 * Math.log10(volumeLinear);
    t.volume = db;
    t.channel.volume.value = db;
  }

  setTrackPan(trackId: string, pan: number): void {
    const t = this.tracks.get(trackId);
    if (!t) return;
    t.pan = Math.max(-1, Math.min(1, pan));
    t.channel.pan.value = t.pan;
  }

  setTrackMute(trackId: string, muted: boolean): void {
    const t = this.tracks.get(trackId);
    if (!t) return;
    t.muted = muted;
    t.channel.mute = muted || (this.anySoloed && !t.soloed);
  }

  setTrackSolo(trackId: string, soloed: boolean): void {
    const t = this.tracks.get(trackId);
    if (!t) return;
    t.soloed = soloed;
    this.updateSoloState();
  }

  private updateSoloState(): void {
    this.anySoloed = Array.from(this.tracks.values()).some((t) => t.soloed);
    for (const t of this.tracks.values()) {
      t.channel.mute = t.muted || (this.anySoloed && !t.soloed);
    }
  }

  // --- VU Meter support (Phase 8) ---

  getMeterNode(trackId: string): Tone.Analyser | null {
    if (this.analysers.has(trackId)) return this.analysers.get(trackId)!;
    const t = this.tracks.get(trackId);
    if (!t) return null;
    const analyser = new Tone.Analyser('waveform', 256);
    t.channel.connect(analyser);
    this.analysers.set(trackId, analyser);
    return analyser;
  }

  getMasterMeterNode(): Tone.Analyser {
    if (this.masterAnalyser) return this.masterAnalyser;
    this.masterAnalyser = new Tone.Analyser('waveform', 256);
    this.masterGain.connect(this.masterAnalyser);
    return this.masterAnalyser;
  }

  // --- Effects integration ---

  async applyTrackEffects(trackId: string, effects: EffectInstance[]): Promise<void> {
    const t = this.tracks.get(trackId);
    const preEffectsGain = this.preEffectsGains.get(trackId);
    if (!t || !preEffectsGain) return;

    // Clear existing chain for this track
    this.effectsManager.clearChain(trackId);

    // Disconnect channel from current target and reconnect through effects
    try { t.channel.disconnect(); } catch { /* ok */ }

    if (effects.length === 0) {
      t.channel.connect(preEffectsGain);
      return;
    }

    // Add effects one by one — effectsManager handles chaining
    for (const effect of effects) {
      await this.effectsManager.addEffect(trackId, effect, t.channel, preEffectsGain);
    }
  }

  async applyMasterEffects(effects: EffectInstance[]): Promise<void> {
    // Clear existing master chain
    this.effectsManager.clearChain('master');

    // Disconnect masterGain from preMasterGain and reconnect through effects
    try { this.masterGain.disconnect(); } catch { /* ok */ }

    if (effects.length === 0) {
      this.masterGain.connect(this.preMasterGain);
      return;
    }

    for (const effect of effects) {
      await this.effectsManager.addEffect('master', effect, this.masterGain, this.preMasterGain);
    }
  }

  get effects(): EffectsManager {
    return this.effectsManager;
  }

  /** Get a decoded audio buffer for preview playback */
  getBuffer(assetId?: string): Tone.ToneAudioBuffer | null {
    if (assetId) return this.buffers.get(assetId) ?? this.buffer;
    return this.buffer;
  }

  // --- Effect Clips (Region-based Event FX) ---

  async applyEffectClips(parentTrackId: string, effectClips: EffectClip[]): Promise<void> {
    const t = this.tracks.get(parentTrackId);
    const preEffectsGain = this.preEffectsGains.get(parentTrackId);
    if (!t || !preEffectsGain) return;

    // Get or create the per-track node map
    if (!this.effectClipNodes.has(parentTrackId)) {
      this.effectClipNodes.set(parentTrackId, new Map());
    }
    const trackNodes = this.effectClipNodes.get(parentTrackId)!;

    // Collect current clip IDs
    const newClipIds = new Set(effectClips.map((ec) => ec.id));

    // Dispose nodes for clips no longer present on THIS track
    for (const [id, node] of trackNodes) {
      if (!newClipIds.has(id)) {
        try { node.toneEffect.disconnect(); } catch { /* ok */ }
        node.toneEffect.dispose();
        trackNodes.delete(id);
      }
    }

    // Create nodes for new clips
    for (const ec of effectClips) {
      if (!trackNodes.has(ec.id)) {
        const toneEffect = await createToneEffect(ec.effectType, ec.params);
        // Set mix to 0 initially (full passthrough) — will be ramped when active
        if (hasMixAPI(toneEffect)) {
          toneEffect.setMix(0);
        } else if ('wet' in toneEffect) {
          (toneEffect as { wet: Tone.Signal<'normalRange'> }).wet.value = 0;
        }
        trackNodes.set(ec.id, {
          toneEffect,
          wetValue: ec.params.wet ?? 0.3,
          active: false,
        });
      } else {
        // Update wet value from params
        const node = trackNodes.get(ec.id)!;
        node.wetValue = ec.params.wet ?? 0.3;
      }
    }

    // Rebuild signal chain for THIS track only: channel → [effectClipNodes...] → preEffectsGain
    try { t.channel.disconnect(); } catch { /* ok */ }
    for (const node of trackNodes.values()) {
      try { node.toneEffect.disconnect(); } catch { /* ok */ }
    }

    // Chain all effect clip nodes for this parent track in order
    type ClipNode = { toneEffect: ToneEffect; wetValue: number; active: boolean };
    const activeNodes: ClipNode[] = [];
    for (const ec of effectClips) {
      const node = trackNodes.get(ec.id);
      if (node) activeNodes.push(node);
    }

    if (activeNodes.length === 0) {
      t.channel.connect(preEffectsGain);
    } else {
      t.channel.connect(activeNodes[0].toneEffect);
      for (let i = 0; i < activeNodes.length - 1; i++) {
        activeNodes[i].toneEffect.connect(activeNodes[i + 1].toneEffect);
      }
      activeNodes[activeNodes.length - 1].toneEffect.connect(preEffectsGain);
    }
  }

  updateEffectClipStates(currentTimeMs: number, effectClips: EffectClip[], parentTrackId?: string): void {
    // Look up nodes across all tracks if no parentTrackId, or scoped to one track
    const trackMaps = parentTrackId
      ? [this.effectClipNodes.get(parentTrackId)].filter(Boolean) as Map<string, { toneEffect: ToneEffect; wetValue: number; active: boolean }>[]
      : Array.from(this.effectClipNodes.values());

    for (const ec of effectClips) {
      let node: { toneEffect: ToneEffect; wetValue: number; active: boolean } | undefined;
      for (const map of trackMaps) {
        node = map.get(ec.id);
        if (node) break;
      }
      if (!node) continue;

      const shouldBeActive = !ec.bypassed &&
        currentTimeMs >= ec.startMs &&
        currentTimeMs < ec.startMs + ec.durationMs;

      if (shouldBeActive && !node.active) {
        node.active = true;
        if (hasMixAPI(node.toneEffect)) {
          node.toneEffect.rampMixTo(node.wetValue, 0.005);
        } else if ('wet' in node.toneEffect) {
          (node.toneEffect as { wet: Tone.Signal<'normalRange'> }).wet.rampTo(node.wetValue, 0.005);
        }
      } else if (!shouldBeActive && node.active) {
        node.active = false;
        if (hasMixAPI(node.toneEffect)) {
          node.toneEffect.rampMixTo(0, 0.005);
        } else if ('wet' in node.toneEffect) {
          (node.toneEffect as { wet: Tone.Signal<'normalRange'> }).wet.rampTo(0, 0.005);
        }
      }
    }
  }

  dispose(): void {
    this.stop();
    this.effectsManager.dispose();
    for (const trackMap of this.effectClipNodes.values()) {
      for (const node of trackMap.values()) {
        node.toneEffect.dispose();
      }
    }
    this.effectClipNodes.clear();
    for (const t of this.tracks.values()) {
      t.player?.dispose();
      t.channel.dispose();
    }
    this.tracks.clear();
    for (const g of this.preEffectsGains.values()) {
      g.dispose();
    }
    this.preEffectsGains.clear();
    for (const a of this.analysers.values()) {
      a.dispose();
    }
    this.analysers.clear();
    this.masterAnalyser?.dispose();
    this.masterAnalyser = null;
    this.preMasterGain.dispose();
    this.masterGain.dispose();
    this.buffer = null;
    this.buffers.clear();
  }
}
